/**
 * StageLights — animated rotating spotlight beams + stationary glow strips.
 *
 * Each beam is a long ConeGeometry with an additive transparent material so
 * it reads like a volumetric light shaft. They rotate slowly around their
 * mount points, sweeping the audience.
 */

import * as THREE from 'three';
import type { StageHandle } from '@/world/stages/StageBase';

const BEAM_COLORS: Record<string, number[]> = {
  'what-stage': [0xff5f6d, 0xffc371, 0xf5f0e8],
  'which-stage': [0x7ce0a3, 0x56c2ff, 0xffc371],
  'this-tent': [0xff5f6d, 0xff8ec0],
  'that-tent': [0x56c2ff, 0xbd72ff],
  'other-stage': [0xbd72ff, 0x56c2ff, 0xff8ec0, 0x7ce0a3],
  'woods': [0xffcb6b, 0xff8ec0]
};

interface BeamGroup {
  group: THREE.Group;
  rate: number;
  axis: 'x' | 'z';
  startPhase: number;
}

export class StageLights {
  readonly group = new THREE.Group();
  private beams: BeamGroup[] = [];

  constructor(stages: StageHandle[]) {
    this.group.name = 'StageLights';
    for (const stage of stages) {
      const colors = BEAM_COLORS[stage.id] ?? [0xffffff];
      this.makeStageRig(stage, colors);
    }
  }

  private makeStageRig(stage: StageHandle, colors: number[]): void {
    // Where to mount the lights — top of stage, slightly behind
    const px = stage.position.x;
    const py = stage.position.y;
    const pz = stage.position.z;

    const trussY = py + 14;

    for (let i = 0; i < colors.length; i++) {
      const color = colors[i % colors.length];
      const t = (i + 0.5) / colors.length;
      const offset = (t - 0.5) * 22;

      // Stage rotation transforms the offset axis. Use the stage group's
      // matrix to find the right position. Simpler: derive direction from
      // stage rotation.
      const yaw = stage.group.rotation.y;
      const localX = offset;
      const localZ = -2;
      const wx = px + Math.cos(yaw) * localX + Math.sin(yaw) * localZ;
      const wz = pz - Math.sin(yaw) * localX + Math.cos(yaw) * localZ;

      const mountPoint = new THREE.Vector3(wx, trussY, wz);

      // Beam — long cone with additive transparent material
      const length = 35;
      const radius = 4;
      const beamGeo = new THREE.ConeGeometry(radius, length, 16, 1, true);
      // Move pivot to the apex (so rotation pivots around mount)
      beamGeo.translate(0, -length / 2, 0);
      const beamMat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.18,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
        fog: false
      });
      const beam = new THREE.Mesh(beamGeo, beamMat);

      // Rotation pivot
      const pivot = new THREE.Group();
      pivot.position.copy(mountPoint);
      pivot.add(beam);

      // Initial tilt: point forward and slightly down toward audience
      pivot.rotation.x = Math.PI; // point down
      pivot.rotation.z = (i - colors.length / 2) * 0.15;

      this.group.add(pivot);

      // Glowing fixture at the mount
      const fixture = new THREE.Mesh(
        new THREE.SphereGeometry(0.35, 8, 6),
        new THREE.MeshStandardMaterial({
          color,
          emissive: color,
          emissiveIntensity: 2.2
        })
      );
      fixture.position.copy(mountPoint);
      this.group.add(fixture);

      this.beams.push({
        group: pivot,
        rate: 0.35 + (i % 3) * 0.18,
        axis: i % 2 === 0 ? 'z' : 'x',
        startPhase: i * 0.7
      });
    }
  }

  update(timeSec: number): void {
    for (const b of this.beams) {
      const sway = Math.sin(timeSec * b.rate + b.startPhase) * 0.45;
      if (b.axis === 'z') {
        b.group.rotation.z = sway + 0.3;
      } else {
        b.group.rotation.x = Math.PI + Math.sin(timeSec * b.rate * 0.8 + b.startPhase) * 0.25;
      }
    }
  }
}
